import { IPost } from 'src/types/hygraph'

/**
 * SEO helpers shared by the blog (`/[pillar]/blog/[slug]`, `/anfitrion/blog/…`)
 * and help-center (`/acerca-de-spotme/[topic]/[slug]`) article pages.
 */

const SITE_URL = 'https://www.spotme.mx'
const BRAND_NAME = 'SpotMe'

/** Seconds between ISR regenerations for Hygraph-backed blog/help pages. */
export const HYGRAPH_PAGE_REVALIDATE_SECONDS = 3600

// Some Hygraph titles were authored as "Foo | SpotMe"; the pages add their own
// branding, so the suffix doubled up in the H1, headline and breadcrumbs.
const TRAILING_BRAND_REGEX = /\s*\|\s*spotme\s*$/i

export const stripBrandSuffix = (title: string | null | undefined): string =>
  (title ?? '').replace(TRAILING_BRAND_REGEX, '').trim()

/** Returns a copy of the post with the trailing "| SpotMe" removed from `title`. */
export const withCleanTitle = <T extends { title?: string | null }>(
  post: T
): T => ({
  ...post,
  title: stripBrandSuffix(post.title)
})

const toTime = (value: string | null | undefined): number =>
  value ? new Date(value).getTime() : NaN

/**
 * `datePublished` = the editorial `date` (what the page shows). Hygraph's
 * `publishedAt` moves on every re-publish (all posts were bulk-republished on
 * 2026-09-27), so it is only a fallback. `dateModified` = `updatedAt`, clamped
 * so it is never earlier than `datePublished`.
 */
export const getPostDates = (
  post: Pick<IPost, 'date' | 'publishedAt' | 'createdAt' | 'updatedAt'>
): { datePublished: string; dateModified: string } => {
  const datePublished: string =
    post.date ?? post.publishedAt ?? post.createdAt ?? ''
  const updatedAt: string | null = post.updatedAt ?? null
  const isModifiedValid =
    !!updatedAt && !(toTime(updatedAt) < toTime(datePublished))
  return {
    datePublished,
    dateModified: isModifiedValid ? (updatedAt as string) : datePublished
  }
}

export type IArticleAuthor = {
  name: string
  url?: string
  type?: 'Person' | 'Organization'
}

/**
 * Posts authored as "SpotMe" (or with no author) are emitted as the
 * Organization, not as a Person named SpotMe.
 */
export const getPostAuthor = (post: Pick<IPost, 'author'>): IArticleAuthor => {
  const name = post.author?.name?.trim()
  if (!name || name.toLowerCase() === BRAND_NAME.toLowerCase()) {
    return { name: BRAND_NAME, url: SITE_URL, type: 'Organization' }
  }
  return { name, type: 'Person' }
}

/** URL prefix Next.js does NOT add to getStaticProps/getServerSideProps redirects. */
export const getLocalePrefix = (locale: string | undefined): string =>
  locale === 'en' ? '/en' : ''
